import type { IUseCase } from '@/shared/contracts/use-case.contract';
import type { IAppDB } from '@/shared/lib/db';

import type { CartItem } from '@/shared/lib/db/schemas/cart.schema';
import { UIError } from '@/shared/utils/ui-error';
import { calculateAddonsPrice } from '../utils/calculate-addons-price';
import { validateAddonsLimit } from '../utils/validate-addons-limit';
import { calculateComboChoicesPrice } from '@/modules/stores/utils/combo';

import { v4 as uuidv4 } from '@lukeed/uuid';

export type UseCaseInput = Omit<CartItem, 'id'>;
export type UseCaseOutput = void;

export class AddItemUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private db: IAppDB;

  constructor(db: IAppDB) {
    this.db = db;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const {
      store_slug,
      observation = '',
      addons = [],
      name,
      quantity,
      price,
      item_id,
      image_id,
      is_reward = false,
      loyalty_points_cost,
      combo_choices,
      category_id,
    } = input;

    if (!quantity) throw new Error('Quantidade do item é obrigatória.');

    if (!store_slug) throw new Error('Slug do estabelecimento é obrigatório.');

    const items = await this.db.items.toArray();

    const hasItemWithDifferentStore = items.some(item => item.store_slug !== store_slug);

    if (hasItemWithDifferentStore) {
      throw new UIError(
        'ITEMS_WITH_DIFFERENT_STORE',
        'Já existe um item de outra loja no carrinho.'
      );
    }

    // Aplica o limite por complemento (issue 311): rejeita a inclusão quando a
    // soma das quantidades de uma categoria ultrapassa seu `select_max`. Default
    // seguro contra seleções que burlem a validação da tela do produto.
    const addonsLimit = validateAddonsLimit(addons);
    if (!addonsLimit.isValid) {
      throw new UIError('ADDON_LIMIT_EXCEEDED', addonsLimit.message);
    }

    // Calcula o preço total automaticamente considerando consider_most_expensive
    const addonsTotalPrice = calculateAddonsPrice(addons);

    // Combo configurável (PRAT-20): as escolhas somam ao preço por unidade.
    const comboChoicesPrice = calculateComboChoicesPrice(combo_choices);

    const totalPrice = (price + addonsTotalPrice + comboChoicesPrice) * quantity;

    // Gera UUID único para o item do carrinho
    const itemId = uuidv4();

    await this.db.items.add({
      id: itemId,
      item_id,
      name,
      price, // Preço do produto (sem addons)
      total_price: totalPrice, // Preço total calculado automaticamente
      store_slug,
      quantity,
      observation,
      image_id, // ID da imagem do produto
      addons,
      is_reward, // Resgate de produto elegível do Clube PratiGO (pago com pontos)
      loyalty_points_cost, // Preço em pontos (apenas para resgates)
      // Escolhas do combo configurável (PRAT-20); só gravadas quando existem.
      ...(combo_choices?.length ? { combo_choices } : {}),
      // Categoria do produto (PRAT-184), para a promoção por quantidade.
      ...(category_id ? { category_id } : {}),
    });
  }
}
